import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';

import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { EventOrder, EventOrderStatus } from '@/modules/eventsV2/types';

import { OrdersTableActions } from './components';

export const getOrdersHistoryColumns = (
  eventId: string,
  onSendSms: (id: string) => void
): ColumnDef<EventOrder>[] => [
  {
    id: 'select',
    size: 40,
    header: ({ table }) => (
      <Checkbox
        className="w-5 h-5"
        onCheckedChange={table.toggleAllRowsSelected}
        checked={table.getIsAllRowsSelected()}
      />
    ),
    cell: ({ row }) => (
      <div className="px-1">
        <Checkbox
          className="w-5 h-5"
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onCheckedChange={row.toggleSelected}
        />
      </div>
    ),
  },
  {
    header: 'Number',
    accessorKey: 'number',
  },
  {
    size: 200,
    header: 'First name',
    accessorKey: 'firstName',
    cell: ({ getValue }) => (getValue() ? getValue() : '–'),
  },
  {
    size: 200,
    header: 'Last name',
    accessorKey: 'lastName',
    cell: ({ getValue }) => (getValue() ? getValue() : '–'),
  },
  {
    size: 240,
    header: 'Phone Number',
    accessorKey: 'phoneNumber',
    cell: ({ getValue }) => (getValue() ? getValue() : '–'),
  },
  {
    header: 'Type',
    accessorKey: 'type',
    cell: ({ getValue }) => (getValue() ? getValue() : '–'),
  },
  {
    header: 'Gender',
    accessorKey: 'gender',
    cell: ({ getValue }) => (getValue() ? getValue() : '–'),
  },

  {
    header: 'Size',
    accessorKey: 'size',
    cell: ({ getValue }) => (getValue() ? getValue() : '–'),
  },
  {
    header: 'Color',
    accessorKey: 'color',
    cell: ({ getValue }) => (getValue() ? getValue() : '–'),
  },
  {
    size: 240,
    header: 'Created at',
    accessorKey: 'createdAt',
    cell: ({ row }) =>
      format(new Date(row.getValue('createdAt')), 'MM/dd/yyyy · hh:mm a'),
  },
  {
    size: 240,
    header: 'Last update',
    accessorKey: 'updatedAt',
    cell: ({ row }) =>
      format(new Date(row.getValue('updatedAt')), 'MM/dd/yyyy · hh:mm a'),
  },
  {
    header: 'Status',
    accessorKey: 'status',
    cell: ({ row }) => {
      const status = row.getValue('status') as EventOrderStatus;

      const statusStyles: Record<EventOrderStatus, string> = {
        [EventOrderStatus.New]: 'border-blue-500 text-blue-500',
        [EventOrderStatus.Completed]: 'border-green-500 text-green-500',
        [EventOrderStatus.Canceled]: 'border-amber-500 text-amber-500',
        [EventOrderStatus.Failed]: 'border-red-500 text-red-500',
      };

      const className = statusStyles[status] || 'border-gray-500 text-gray-500';

      return (
        <Badge variant="outline" className={className}>
          {row.getValue('status')}
        </Badge>
      );
    },
  },
  {
    size: 100,
    accessorKey: 'imageUrl',
    header: 'Image',
    cell: ({ row }) =>
      row.getValue('imageUrl') ? (
        <img
          src={row.getValue('imageUrl')}
          alt="Preview"
          className="w-[30px] h-[34px] rounded-lg"
        />
      ) : (
        <p className="text-sm text-zinc-500">No image</p>
      ),
  },
  {
    size: 40,
    id: 'actions',
    header: 'Actions',
    enableHiding: false,
    cell: ({ row }) => (
      <OrdersTableActions
        eventId={eventId}
        orderId={row.original.id}
        imageUrl={row.original.imageUrl}
        onSendSms={() => onSendSms(row.original.id)}
      />
    ),
  },
];
